import { useState } from 'react';
import type { IconType } from 'react-icons';
import { MdContentCopy, MdDelete, MdEdit } from 'react-icons/md';

import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import type AgentApiCandidateMailTemplatesRecord from '@/types/serializers/AgentApiCandidateMailTemplatesRecord';
import useDeleteCandidateMailTemplate from '../api/useDeleteCandidateMailTemplate';
import MailDialog from './MailDialog';
import MailPanelHeader from './MailPanelHeader';
import MailVariableBody from './MailVariableBody';

type CandidateMailTemplateDetailProps = {
  template?: AgentApiCandidateMailTemplatesRecord;
  isLoading: boolean;
  onBack: () => void;
  onEdit: (templateId: number) => void;
  onDuplicate: (templateId: number) => void;
};

function IconAction({
  icon: Icon,
  label,
  onClick,
}: {
  icon: IconType;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="flex size-5 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-0 text-sub transition-colors hover:text-main"
    >
      <Icon className="size-5" />
    </button>
  );
}

export default function CandidateMailTemplateDetail({
  template,
  isLoading,
  onBack,
  onEdit,
  onDuplicate,
}: CandidateMailTemplateDetailProps) {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const { mutateAsync: deleteTemplate, isPending: isDeleting } = useDeleteCandidateMailTemplate();

  if (!template) {
    return (
      <div className="flex w-full flex-col">
        <MailPanelHeader onBack={onBack} title="" />
        <div className="flex justify-center py-24">
          {isLoading ? (
            <Loading size="small" />
          ) : (
            <p className="text-body-sm text-sub">テンプレートが見つかりません。</p>
          )}
        </div>
      </div>
    );
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteTemplate({ id: template.id });
      showSuccessToast('テンプレートを削除しました');
      setIsDeleteConfirmOpen(false);
      onBack();
    } catch (error) {
      showErrorToast('テンプレートの削除に失敗しました');
    }
  };

  return (
    <div className="flex w-full flex-col">
      <MailPanelHeader
        onBack={onBack}
        title={<span className="truncate">{template.name}</span>}
        actions={
          <div className="flex items-center gap-sm">
            <IconAction icon={MdEdit} label="編集" onClick={() => onEdit(template.id)} />
            <IconAction
              icon={MdContentCopy}
              label="複製"
              onClick={() => onDuplicate(template.id)}
            />
            <IconAction icon={MdDelete} label="削除" onClick={() => setIsDeleteConfirmOpen(true)} />
          </div>
        }
      />
      <div className="flex flex-col gap-sm px-xl pb-xl">
        <p className="text-heading-xxs break-words text-main">{template.subject}</p>
        <div className="h-px w-full bg-border" />
        <MailVariableBody body={template.body} />
      </div>

      <MailDialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title="テンプレートを削除しますか？"
        widthClassName="w-[456px]"
        preventClose={isDeleting}
        footer={
          <>
            <Button
              type="button"
              color="secondary"
              size="md"
              disabled={isDeleting}
              onClick={() => setIsDeleteConfirmOpen(false)}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              color="danger"
              size="md"
              disabled={isDeleting}
              onClick={() => void handleConfirmDelete()}
            >
              削除する
            </Button>
          </>
        }
      >
        <p className="text-body-sm break-words text-main">
          「{template.name}」を削除します。削除したテンプレートは元に戻せません。
        </p>
      </MailDialog>
    </div>
  );
}
